import { useState, useEffect, useRef } from 'react'

export const Graph = ({ children }) => {
  return <div className="flex flex-col gap-10">{children}</div>
}

const CenterItem = ({ children }) => {
  return <div className="flex justify-center items-center gap-4">{children}</div>
}

const AnimatedPaths = ({ paths }) => {
  return (
    <svg
      className="absolute top-0 left-0 w-full h-full pointer-events-none z-0"
      preserveAspectRatio="none"
    >
      <linearGradient
        id="circleGradient"
        x1="0%"
        y1="0%"
        x2="100%"
        y2="0%"
        gradientTransform="rotate(163.8)"
      >
        <stop offset="47.26%" stopColor="#5DD39E" />
      </linearGradient>
      {paths.map((path, index) => {
        const animationDuration = Math.random() * 4 + 4
        const delay = Math.random() * 3
        const { d, isTopLeaf } = path

        return (
          <g key={index}>
            <path d={d} fill="none" stroke="#CAD8DE" strokeWidth="2" strokeOpacity="1" />
            <circle r="3" fill="url(#circleGradient)">
              <animateMotion
                dur={`${animationDuration}s`}
                repeatCount="indefinite"
                path={d}
                begin={`${delay}s`}
                calcMode="splines"
                keyTimes="0;0.5;1"
                keyPoints={isTopLeaf ? '0;0.5;1' : '1;0.5;0'}
                keySplines={isTopLeaf ? '0.6 1 0.4 0' : '0.4 0 0.6 1'}
              />
              <animate
                attributeName="opacity"
                values="0.1;1;0.1"
                begin={`${delay}s`}
                dur={`${animationDuration}s`}
                repeatCount="indefinite"
                keyTimes="0;0.5;1"
              />
            </circle>
          </g>
        )
      })}
    </svg>
  )
}

interface Paths {
  d: string
  isTopLeaf: boolean
}

const getRelativePosition = (
  element: HTMLElement,
  containerRef: React.RefObject<HTMLDivElement>,
) => {
  if (!containerRef.current) return { x: 0, y: 0 }

  const containerRect = containerRef.current.getBoundingClientRect()
  const elementRect = element.getBoundingClientRect()

  return {
    x: elementRect.left - containerRect.left + elementRect.width / 2,
    y: elementRect.top - containerRect.top + elementRect.height / 2,
  }
}

interface GraphConnectorProps {
  topLeaves: React.ReactNode[]
  bottomLeaves: React.ReactNode[]
  centerItem: React.ReactNode
  className?: string
}

const GraphConnector: React.FC<GraphConnectorProps> = ({
  topLeaves,
  bottomLeaves,
  centerItem,
  className = '',
}) => {
  const [paths, setPaths] = useState<Paths[]>([])
  const topLeavesRefs = useRef<(HTMLDivElement | null)[]>(new Array(topLeaves.length).fill(null))
  const bottomLeavesRefs = useRef<(HTMLDivElement | null)[]>(
    new Array(bottomLeaves.length).fill(null),
  )
  const centerItemRef = useRef<HTMLDivElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const calculatePaths = () => {
      if (!centerItemRef.current || !containerRef.current) return

      const centerPosition = getRelativePosition(centerItemRef.current, containerRef)
      const centerX = centerPosition.x
      const centerY = centerPosition.y

      const topPathes = (topLeavesRefs.current || [])
        .filter(ref => ref !== null)
        .map(ref => {
          if (!ref) return null

          const leafPosition = getRelativePosition(ref, containerRef)
          const leafX = leafPosition.x
          const leafY = leafPosition.y

          const d = `
            M${leafX},${leafY}
            C${leafX},${(leafY + centerY) / 2}
              ${centerX},${(leafY + centerY) / 2}
              ${centerX},${centerY}
          `

          return {
            d,
            isTopLeaf: true,
          }
        })
        .filter(path => path !== null)

      const bottomPathes = (bottomLeavesRefs.current || [])
        .filter(ref => ref !== null)
        .map(ref => {
          if (!ref) return null

          const leafPosition = getRelativePosition(ref, containerRef)
          const leafX = leafPosition.x
          const leafY = leafPosition.y

          const d = `
            M${leafX},${leafY}
            C${leafX},${(leafY + centerY) / 2}
              ${centerX},${(leafY + centerY) / 2}
              ${centerX},${centerY}
          `

          return {
            d,
            isTopLeaf: false,
          }
        })
        .filter(path => path !== null)

      setPaths([...topPathes, ...bottomPathes] as Paths[])
    }

    // Delay to wait for the DOM to render,
    // so we can get the correct position of the elements
    setTimeout(calculatePaths, 10)

    window.addEventListener('resize', calculatePaths)

    return () => {
      window.removeEventListener('resize', calculatePaths)
    }
  }, [topLeaves, bottomLeaves, centerItem])

  return (
    <div className={`relative ${className} flex flex-col gap-20`} ref={containerRef}>
      <AnimatedPaths paths={paths} />

      {/* Top Leaves */}
      {topLeaves.length > 0 && (
        <div className="flex justify-center flex-wrap gap-4 relative z-10">
          {topLeaves.map((leaf, index) => (
            <div
              key={index}
              ref={el => {
                if (topLeavesRefs.current) {
                  topLeavesRefs.current[index] = el
                }
              }}
              className="relative z-10"
            >
              {leaf}
            </div>
          ))}
        </div>
      )}

      {/* Center Item */}
      <div ref={centerItemRef} className="relative z-10">
        {centerItem}
      </div>

      {/* Bottom Leaves */}
      {bottomLeaves.length > 0 && (
        <div className="flex justify-center flex-wrap-reverse gap-4 relative z-10">
          {bottomLeaves.map((leaf, index) => (
            <div
              key={index}
              ref={el => {
                if (bottomLeavesRefs.current) {
                  bottomLeavesRefs.current[index] = el
                }
              }}
              className="relative z-10"
            >
              {leaf}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

Graph.CenterItem = CenterItem
Graph.Connector = GraphConnector
